import React, { useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';

import {
  coolingSystemCloneForCreate,
  coolingSystemCreateDraft,
  coolingSystemEnsureNameForCreate,
} from './subsections/coolingSystemEngineBindings';
import { CoolingSystemEngineEditFormBody } from './subsections/CoolingSystemEngineEditFormBody';
import {
  isCoolingPersistedProps,
  type CoolingProps,
  type CoolingPersistedProps,
  type CoolingInitialProps,
} from './useCooling';
import { buildLegacyCoolingSystemFromRequestFields } from '../../../components/coolingsystems/coolingSystemListUtils';
import { Eye, EyeOff } from '../../../components/icons';
import { PersistedStepHeader } from '../../../components/PersistedStepHeader';
import { StepShell } from '../../../components/StepShell';
import { useCoolingSystemConfigurations } from '../../../hooks/queries/useCoolingSystemConfigurations';
import {
  coolingSystemToDescriptorInstance,
  descriptorInstanceToCoolingSystem,
} from '../../../services/descriptorEditSessionFlush';
import { COOLING_SYSTEM_DESCRIPTOR } from '../../sections/coolingSystem.descriptor';
import {
  DescriptorEngineSection,
  type DescriptorInstance,
} from '../../sections/descriptorEngineAdapter';

import type { CoolingSystemConfigurationsSaveHandle } from './subsections/coolingSystemConfigurationsSaveHandle';
import type { RepeatableInstanceEditFormRenderProps } from '../../../components/repeatableSections/repeatableInstanceEditFormTypes';
import type { RepeatableInstanceSaveHandle } from '../../../components/repeatableSections/repeatableInstanceSaveHandle';
import type { CoolingSystemConfiguration } from '../../../types/coolingSystem';

const CoolingInitialLayout: React.FC<CoolingInitialProps> = ({
  formData,
  updateFormData,
  requestId,
  disabled = false,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  isEditing = false,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  registerCoolingSystemSaveHandle,
  onCoolingSystemEditorActiveChange,
}) => {
  const stage = String(formData.stage ?? formData.sysRequestStage ?? 'DRAFT');

  // #997 Story 6.6 — the create/draft layout runs on the descriptor engine
  // (mirror Large UPS 6.5). The engine owns the list/edit-session/save/flush/
  // attachment lifecycle (6.1 per-unit scoping + 6.2 temp→real remap now run
  // live); Cooling supplies its bespoke view (`CoolingSystemEngineEditFormBody`
  // → `CoolingSystemEditFormContent`, both per-unit carousels) and its proven
  // draft/name/clone semantics (`coolingSystemEngineBindings`) through the
  // engine's per-section seams, so the user-visible editor is unchanged. The
  // draft list stays parent-owned in `formData.coolingSystemConfigurations`.
  const draftCoolingSystems =
    (formData.coolingSystemConfigurations as
      | CoolingSystemConfiguration[]
      | undefined) ?? [];
  const draftInstances = useMemo(
    () =>
      draftCoolingSystems.map(
        unit => coolingSystemToDescriptorInstance(unit) as DescriptorInstance,
      ),
    [draftCoolingSystems],
  );
  const requestIdNum = requestId ? Number.parseInt(requestId, 10) || 0 : 0;
  const handleDraftInstancesChange = useCallback(
    (instances: DescriptorInstance[]) =>
      updateFormData(
        'coolingSystemConfigurations',
        instances.map(instance =>
          descriptorInstanceToCoolingSystem(
            instance,
            requestIdNum,
            COOLING_SYSTEM_DESCRIPTOR,
          ),
        ),
      ),
    [updateFormData, requestIdNum],
  );

  // Engine handle → bespoke CoolingSystemConfigurationsSaveHandle (CreateRequest's
  // footer-save/dismiss/scope-removal bridge keeps its existing contract).
  const registerInstanceSaveHandle = useMemo(() => {
    if (!registerCoolingSystemSaveHandle) {
      return undefined;
    }
    return (handle: RepeatableInstanceSaveHandle | null) => {
      if (!handle) {
        registerCoolingSystemSaveHandle(null);
        return;
      }
      const coolingHandle: CoolingSystemConfigurationsSaveHandle = {
        isCoolingSystemEditorOpen: handle.isInstanceEditorOpen,
        hasUnsavedCoolingSystemEditor: handle.hasUnsavedInstanceEditor,
        commitOpenCoolingSystemEditor: handle.commitOpenInstanceEditor,
        saveUnsavedCoolingSystemIfNeeded: async () => {
          if (
            !handle.isInstanceEditorOpen() ||
            !handle.hasUnsavedInstanceEditor()
          ) {
            return true;
          }
          return handle.commitOpenInstanceEditor();
        },
        hasPendingCoolingSystemChanges: () =>
          handle.hasPendingListChanges?.() ?? false,
        flushPendingCoolingSystemChanges: async () => {
          await handle.flushPendingListChanges?.();
        },
        tryDismissCoolingSystemEditor: handle.tryDismissInstanceEditor,
        discardOpenCoolingSystemEditor: handle.discardOpenInstanceEditor,
        // Draft list is parent-owned (formData.coolingSystemConfigurations) —
        // scope removal strips it there; the in-memory session reset is the
        // persisted-path concern.
        resetSessionAfterScopeRemoval: () => {},
      };
      registerCoolingSystemSaveHandle(coolingHandle);
    };
  }, [registerCoolingSystemSaveHandle]);

  const editBody = useCallback(
    (props: RepeatableInstanceEditFormRenderProps) => (
      <CoolingSystemEngineEditFormBody {...props} showAdditionalInformation />
    ),
    [],
  );

  return (
    <StepShell
      title="Cooling"
      testID="create-form-header-cooling"
      
    >
      <DescriptorEngineSection
        descriptor={COOLING_SYSTEM_DESCRIPTOR}
        requestId={requestId}
        isEditing={isEditing || !disabled}
        disabled={disabled}
        requestStage={stage}
        draftInstances={draftInstances}
        onDraftInstancesChange={handleDraftInstancesChange}
        legacyRequestFields={formData}
        attachmentsBySection={attachmentsBySection}
        onAttachFiles={onAttachFiles}
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={selectedFiles}
        onFilesSelected={onFilesSelected}
        offlineMode={offlineMode}
        editBody={editBody}
        createDraft={coolingSystemCreateDraft}
        ensureNameForCreate={coolingSystemEnsureNameForCreate}
        cloneForCreate={coolingSystemCloneForCreate}
        registerInstanceSaveHandle={registerInstanceSaveHandle}
        onInstanceEditorActiveChange={onCoolingSystemEditorActiveChange}
        testID="cooling-system-configurations"
      />
    </StepShell>
  );
};

const CoolingPersistedLayout: React.FC<CoolingPersistedProps> = ({
  request,
  isEditing,
  editedData,
  showFullDetails = true,
  showViewToggle,
  isAdvancedView,
  onToggleView,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  onCaptionChange,
  pendingCaptionEdits = {},
  registerInstanceSaveHandle,
  applyEditedDataPatch,
  coolingSystemConfigurationsDirty = false,
  onCoolingSystemEditorActiveChange,
}) => {
  const requestIdStr = String(request.id ?? '');
  const fieldDisabled = !isEditing;
  const stage = String(request.stage || request.sysRequestStage || 'DRAFT');
  const isOfflineRequest =
    request.id != null && String(request.id).startsWith('o-');

  const pdfFormData = useMemo(
    () => ({
      ...(request as unknown as Record<string, unknown>),
      ...(isEditing ? editedData : {}),
    }),
    [request, editedData, isEditing],
  );

  // #997 Story 6.6 — the persisted layout runs on the descriptor engine (mirror
  // Large UPS). Host-boundary display-list resolution: while the user has Cooling
  // edits this session, `editedData.coolingSystemConfigurations` is authoritative
  // (even when empty — deleted-all must not resurrect); otherwise units query >
  // request embed > legacy flat-unit synthesis.
  const { data: queryUnits = [] } = useCoolingSystemConfigurations(
    requestIdStr,
    true,
  );
  const editedUnits = editedData?.coolingSystemConfigurations;
  const effectiveUnits = useMemo((): CoolingSystemConfiguration[] => {
    if (
      isEditing &&
      coolingSystemConfigurationsDirty &&
      Array.isArray(editedUnits)
    ) {
      return editedUnits as CoolingSystemConfiguration[];
    }
    if (queryUnits.length > 0) {
      return queryUnits;
    }
    const embed = pdfFormData.coolingSystemConfigurations;
    if (Array.isArray(embed) && embed.length > 0) {
      return embed as CoolingSystemConfiguration[];
    }
    const legacyUnit = buildLegacyCoolingSystemFromRequestFields(
      pdfFormData,
      requestIdStr,
    );
    return legacyUnit ? [legacyUnit] : [];
  }, [
    isEditing,
    coolingSystemConfigurationsDirty,
    editedUnits,
    queryUnits,
    pdfFormData,
    requestIdStr,
  ]);

  const legacyRequestFields = useMemo(
    () => ({ ...pdfFormData, coolingSystemConfigurations: effectiveUnits }),
    [pdfFormData, effectiveUnits],
  );

  const editBody = useCallback(
    (props: RepeatableInstanceEditFormRenderProps) => (
      <CoolingSystemEngineEditFormBody
        {...props}
        showAdditionalInformation
        showFullDetails={showFullDetails}
      />
    ),
    [showFullDetails],
  );

  return (
    <View style={coolingPersistedStyles.container}>
      <PersistedStepHeader
        title="Cooling"
        uomSlot={null}
        actionsSlot={
          showViewToggle && onToggleView ? (
            <TouchableOpacity
              style={coolingPersistedStyles.viewToggleButton}
              onPress={onToggleView}
            >
              {isAdvancedView ? (
                <Eye size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              ) : (
                <EyeOff size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              )}
              <Text style={coolingPersistedStyles.viewToggleText}>
                {isAdvancedView ? 'Advanced' : 'Basic'}
              </Text>
            </TouchableOpacity>
          ) : null
        }
      />

      <DescriptorEngineSection
        descriptor={COOLING_SYSTEM_DESCRIPTOR}
        requestId={requestIdStr}
        isEditing={isEditing}
        // #997 QA-C: keep view-mode rows drillable; mutations stay gated by
        // isEditing/listReadOnly, so do NOT restore fieldDisabled (=!isEditing) here.
        disabled={false}
        requestStage={stage}
        showFullDetails={showFullDetails}
        legacyRequestFields={legacyRequestFields}
        applyEditedDataPatch={applyEditedDataPatch}
        attachmentsBySection={attachmentsBySection}
        onAttachFiles={onAttachFiles}
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={selectedFiles}
        onFilesSelected={onFilesSelected}
        offlineMode={isOfflineRequest || offlineMode}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        editBody={editBody}
        createDraft={coolingSystemCreateDraft}
        ensureNameForCreate={coolingSystemEnsureNameForCreate}
        cloneForCreate={coolingSystemCloneForCreate}
        registerInstanceSaveHandle={registerInstanceSaveHandle}
        onInstanceEditorActiveChange={onCoolingSystemEditorActiveChange}
        testID="cooling-system-configurations"
      />
    </View>
  );
};

const coolingPersistedStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  viewToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Platform.OS === 'web' ? 12 : 8,
    paddingVertical: Platform.OS === 'web' ? 6 : 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f8f9fa',
  },
  viewToggleText: {
    fontSize: Platform.OS === 'web' ? 13 : 11,
    fontWeight: '500',
    color: '#666',
  },
});

export const Cooling: React.FC<CoolingProps> = props =>
  isCoolingPersistedProps(props) ? (
    <CoolingPersistedLayout {...props} />
  ) : (
    <CoolingInitialLayout {...props} />
  );

Cooling.displayName = 'Cooling';

/** @deprecated Test / caption suites — compose path only */
export const CoolingReadinessStep = CoolingInitialLayout;
/** @deprecated Test / caption suites — persisted request detail branch */
export const CoolingInRequest = CoolingPersistedLayout;
